import { supabase } from './supabase'
import type { EscolaR065Parsed } from './parseR065Pdf'

export interface ImportarMatriculasParams {
  escolas: EscolaR065Parsed[]
  mesReferencia: string
  anoLetivo: number
  arquivoNome: string
  userId: string
}

function migrationHint(message: string) {
  if (message.includes('matriculas_importacoes') || message.includes('matriculas_escola')) {
    return (
      'Tabelas de matrículas ainda não existem no banco. ' +
      'Abra o Supabase SQL Editor e execute o arquivo supabase/migrations/20260714220000_matriculas_r065.sql'
    )
  }
  return message
}

export async function verificarMesImportacao(mesReferencia: string) {
  const { data, error } = await supabase
    .from('matriculas_importacoes')
    .select('id')
    .eq('mes_referencia', mesReferencia)
    .maybeSingle()
  if (error) throw new Error(migrationHint(error.message))
  return !!data
}

/** Mapeia a escola lida do PDF para as colunas de matriculas_escola. */
export function toRow(importacaoId: string, e: EscolaR065Parsed, escolaId: string | null) {
  return {
    importacao_id: importacaoId,
    escola_id: escolaId,
    codigo_semed: Number(e.codigo),
    nome_pdf: e.nome,
    tipo_unidade: e.tipo,
    infantil_m: e.infantil.m,
    infantil_t: e.infantil.t,
    infantil_n: e.infantil.n,
    infantil_i: e.infantil.i,
    infantil_total: e.infantil.total,
    fund_menor_m: e.fundMenor.m,
    fund_menor_t: e.fundMenor.t,
    fund_menor_n: e.fundMenor.n,
    fund_menor_i: e.fundMenor.i,
    fund_menor_total: e.fundMenor.total,
    fund_maior_m: e.fundMaior.m,
    fund_maior_t: e.fundMaior.t,
    fund_maior_n: e.fundMaior.n,
    fund_maior_i: e.fundMaior.i,
    fund_maior_total: e.fundMaior.total,
    eja_m: e.eja.m,
    eja_t: e.eja.t,
    eja_n: e.eja.n,
    eja_i: e.eja.i,
    eja_total: e.eja.total,
    total_escola: e.totalEscola,
  }
}

export async function importarMatriculasR065(params: ImportarMatriculasParams) {
  const { escolas, mesReferencia, anoLetivo, arquivoNome, userId } = params
  if (!escolas.length) throw new Error('Nenhuma escola no PDF')
  if (!mesReferencia) throw new Error('Informe o mês de referência')

  const { data: existente } = await supabase
    .from('matriculas_importacoes')
    .select('id')
    .eq('mes_referencia', mesReferencia)
    .maybeSingle()

  if (existente) {
    const { error: delErr } = await supabase.from('matriculas_importacoes').delete().eq('id', existente.id)
    if (delErr) throw new Error(migrationHint(delErr.message))
  }

  const { data: importacao, error: impErr } = await supabase
    .from('matriculas_importacoes')
    .insert({
      mes_referencia: mesReferencia,
      ano_letivo: anoLetivo,
      arquivo_nome: arquivoNome,
      importado_por: userId,
    })
    .select('id')
    .single()

  if (impErr || !importacao) throw new Error(migrationHint(impErr?.message || 'Erro ao criar importação'))

  const { data: escolasDb, error: escErr } = await supabase.from('escolas').select('id, codigo_semed')
  if (escErr) throw new Error(migrationHint(escErr.message))

  const byCodigo = new Map((escolasDb || []).map((e) => [String(e.codigo_semed), e.id as string]))
  const rows = escolas.map((e) => toRow(importacao.id, e, byCodigo.get(e.codigo) ?? null))

  const chunkSize = 50
  for (let i = 0; i < rows.length; i += chunkSize) {
    const chunk = rows.slice(i, i + chunkSize)
    const { error: rowErr } = await supabase.from('matriculas_escola').insert(chunk)
    if (rowErr) throw new Error(migrationHint(rowErr.message))
  }

  const { error: syncErr } = await supabase.rpc('sync_escolas_matriculas', {
    p_importacao_id: importacao.id,
  })
  if (syncErr) throw new Error(migrationHint(syncErr.message))

  return { imported: rows.length, importacao_id: importacao.id, mes_referencia: mesReferencia }
}
